import { useEffect } from "react";
import PropTypes from "prop-types";
import { showToast } from "../../../../utils/utils";
import { InvoiceDetails, responseStatus } from "../../../../locales/en";
import { getJobsByIdURL } from "../../../../utils/constants/apiEndPoints";
import privateAxios from "../../../../utils/axiosPrivate";
import { showToastType } from "../../../../utils/constants/appConstants";

// Define constants to match backend JobSupervisor constants
const JOB_STATUS = {
  CREATED: "CREATED",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED"
};

// A failed job's message tells the customer what to do next (for example
// "Please reconnect Xero"), so it must stay on screen as an error toast.
const TOAST_TYPE_BY_JOB_STATUS = {
  [JOB_STATUS.IN_PROGRESS]: responseStatus.info,
  [JOB_STATUS.FAILED]: responseStatus.error
};

const XeroInvoiceSync = (props) => {
  const { jobId, setXeroInvoiceJobId, onJobFailed } = props;

  useEffect(() => {
    if (!jobId) return;

    const progressEndpoint = getJobsByIdURL(jobId);
    // A request slower than the poll interval can still be in flight when a
    // later response has already reported a terminal status; whatever it
    // returns is stale, so it is discarded.
    let settled = false;
    const interval = setInterval(async () => {
      try {
        const response = await privateAxios.get(progressEndpoint);
        if (settled) return;
        const progressData = response?.data?.data;

        if (!progressData) return;

        const progressMessage =
          progressData?.message || `${InvoiceDetails.xeroWriteBack.message}`;
        showToast(
          progressMessage,
          TOAST_TYPE_BY_JOB_STATUS[progressData.status] ||
            responseStatus.success
        );

        if (
          [JOB_STATUS.COMPLETED, JOB_STATUS.FAILED].includes(
            progressData.status
          )
        ) {
          settled = true;
          clearInterval(interval);
          localStorage.removeItem("xeroInvoiceSyncJobId");
          setXeroInvoiceJobId(null);
          if (progressData.status === JOB_STATUS.FAILED) {
            // A failed sync can mean the accounting software was disconnected,
            // so the page re-reads the connection state and offers Connect
            // while the failure message stays on screen.
            onJobFailed?.();
          }
        }
      } catch (error) {
        if (settled) return;
        showToast(error?.response?.data?.message, showToastType?.error);
      }
    }, 3000);

    // A request in flight at cleanup resolves after the poller is gone, so it
    // is marked settled and its response is discarded.
    return () => {
      settled = true;
      clearInterval(interval);
    };
  }, [jobId, privateAxios, setXeroInvoiceJobId]);

  return null;
};
XeroInvoiceSync.propTypes = {
  jobId: PropTypes.string,
  setXeroInvoiceJobId: PropTypes.func.isRequired,
  onJobFailed: PropTypes.func
};

export default XeroInvoiceSync;
